import React, { FC } from 'react';
import { Badge, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Lock, Security } from '@material-ui/icons';

import { useGameListItemData } from './useGameListItemData';
import { Airing, Fer, USE_DRM } from '../../utils/types';
import StatusChip, { statusChipStyles } from '../common/StatusChip';

const useStyles = makeStyles(() => ({
	channelList: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
		paddingBottom: 5
	},
	channelListItem: {
		marginBottom: 3
	},
	badgePrimary: {
		right: 3,
		top: 7,
		'& svg': {
			fontSize: '0.9rem'
		}
	},
	badgeSecondary: {
		right: 3,
		top: 24,
		'& svg': {
			fontSize: '0.9rem'
		}
	},
	ferSection: {
		margin: 0
	},
	ferSectionItem: {
		listStyleType: 'none'
	}
}));

type Props = {
	gameId: string;
	useDrm: USE_DRM;
	gameStatus?: string | null;
};

const GameListItemAirings: FC<Props> = ({ gameId, useDrm, gameStatus }) => {
	const classes = useStyles();
	const statusChipClasses = statusChipStyles();

	const { airings, fers, loading, error } = useGameListItemData(gameId, gameStatus);

	if (loading) return <CircularProgress size={16} />;
	if (error) return <>-</>;
	if (airings.length === 0 && fers.length === 0) return <>None</>;

	const renderAiringRow = (airing: Airing): JSX.Element => {
		const { alternateIds, channel, drm, id, status } = airing;

		const isProtected = typeof alternateIds?.items.find((altId): boolean => altId.namespace === 'PROTECTED_AIRING') !== 'undefined';
		const isDrmActive = [USE_DRM.YES, USE_DRM.BOTH].includes(useDrm);
		const isDrm = !!drm;

		const chip = ((): JSX.Element => {
			if (isProtected && isDrmActive && isDrm) {
				return (
					<Badge
						overlap='rectangular'
						classes={{ badge: classes.badgePrimary }}
						badgeContent={
							<Tooltip title='DRM Protected'>
								<Security color='action' />
							</Tooltip>
						}>
						<Badge
							overlap='rectangular'
							classes={{ badge: classes.badgeSecondary }}
							badgeContent={
								<Tooltip title='Protected Airing'>
									<Lock color='action' />
								</Tooltip>
							}>
							<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
						</Badge>
					</Badge>
				);
			}
			if (isProtected && (!isDrmActive || !isDrm)) {
				return (
					<Badge
						classes={{ badge: classes.badgePrimary }}
						badgeContent={
							<Tooltip title='Protected Airing'>
								<Lock color='action' />
							</Tooltip>
						}>
						<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
					</Badge>
				);
			}
			if (!isProtected && isDrmActive && isDrm) {
				return (
					<Badge
						classes={{ badge: classes.badgePrimary }}
						badgeContent={
							<Tooltip title='DRM Protected'>
								<Security color='action' />
							</Tooltip>
						}>
						<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
					</Badge>
				);
			}
			return <StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />;
		})();

		return (
			<li key={id} className={classes.channelListItem}>
				{chip}
			</li>
		);
	};

	const renderFerRow = (fer: Fer): JSX.Element => {
		return (
			<li key={`fer_${fer.ferId}`} className={classes.channelListItem}>
				<StatusChip
					className={statusChipClasses.bigChip}
					status={fer.available ? 'AVAILABLE' : 'NOT_AVAILABLE'}
					name={fer.channel?.name || 'Unknown FER'}
				/>
			</li>
		);
	};

	return (
		<>
			{airings.length > 0 && <ul className={classes.channelList}>{airings.map((airing) => renderAiringRow(airing))}</ul>}
			{fers.length > 0 && <ul className={`${classes.channelList} ${classes.ferSection}`}>{fers.map((fer) => renderFerRow(fer))}</ul>}
		</>
	);
};

export default GameListItemAirings;
